// Everything she says and everything it remembers lives here, in this browser only.
import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import type { JournalEntry, Memory, Message } from '@/types';

interface PuppyDB extends DBSchema {
  messages: { key: string; value: Message; indexes: { byAt: number } };
  memories: { key: string; value: Memory; indexes: { byKind: string } };
  journal: { key: string; value: JournalEntry };
}

let dbPromise: Promise<IDBPDatabase<PuppyDB>> | undefined;

function db() {
  dbPromise ??= openDB<PuppyDB>('puppy', 1, {
    upgrade(database) {
      database.createObjectStore('messages', { keyPath: 'id' }).createIndex('byAt', 'at');
      database.createObjectStore('memories', { keyPath: 'id' }).createIndex('byKind', 'kind');
      database.createObjectStore('journal', { keyPath: 'date' });
    },
  });
  return dbPromise;
}

export const repo = {
  async loadAll() {
    const d = await db();
    const [messages, memories, journal] = await Promise.all([d.getAllFromIndex('messages', 'byAt'), d.getAll('memories'), d.getAll('journal')]);
    return { messages, memories, journal: journal.sort((a, b) => a.date.localeCompare(b.date)) };
  },

  async putMessage(m: Message) { await (await db()).put('messages', m); },
  async deleteMessage(id: string) { await (await db()).delete('messages', id); },

  async putMemory(m: Memory) { await (await db()).put('memories', m); },
  async putMemories(list: Memory[]) {
    const tx = (await db()).transaction('memories', 'readwrite');
    for (const m of list) await tx.store.put(m);
    await tx.done;
  },
  async deleteMemory(id: string) { await (await db()).delete('memories', id); },

  async putJournal(e: JournalEntry) { await (await db()).put('journal', e); },

  async clearAll() {
    const d = await db();
    const tx = d.transaction(['messages', 'memories', 'journal'], 'readwrite');
    await Promise.all([tx.objectStore('messages').clear(), tx.objectStore('memories').clear(), tx.objectStore('journal').clear()]);
    await tx.done;
  },

  async replaceAll(data: { messages: Message[]; memories: Memory[]; journal: JournalEntry[] }) {
    const d = await db();
    const tx = d.transaction(['messages', 'memories', 'journal'], 'readwrite');
    await Promise.all([tx.objectStore('messages').clear(), tx.objectStore('memories').clear(), tx.objectStore('journal').clear()]);
    for (const m of data.messages) await tx.objectStore('messages').put(m);
    for (const m of data.memories) await tx.objectStore('memories').put(m);
    for (const j of data.journal) await tx.objectStore('journal').put(j);
    await tx.done;
  },
};
